import React from 'react';

export function StoreCard({
  store,
  config = {},
  isSelected = false,
  onSelect,
  className = '',
  ...props
}) {
  const handleClick = () => {
    onSelect && onSelect(store.id);
  };

  // Helper function to format hours
  const formatHours = (hours) => {
    if (typeof hours === 'string') return hours;
    if (typeof hours === 'object') {
      const today = new Date().toLocaleLowerCase().substring(0, 3);
      const dayMapping = {
        sun: 'sunday',
        mon: 'monday',
        tue: 'tuesday',
        wed: 'wednesday',
        thu: 'thursday',
        fri: 'friday',
        sat: 'saturday'
      };
      const fullDay = dayMapping[today] || 'monday';
      return hours[fullDay] || 'Hours not available';
    }
    return 'Hours not available';
  };

  return (
    <div 
      className={`bg-surface border border-border rounded-2xl p-6 shadow-md transition-all hover:shadow-lg cursor-pointer transform hover:-translate-y-1 ${
        isSelected ? 'ring-2 ring-accent shadow-lg' : ''
      } ${className}`} 
      onClick={handleClick}
      {...props}
    >
      {/* Header with store name and manager info */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-heading text-xl font-bold text-primary mb-1">{store.name}</h3>
          {store.brand && (
            <div className="text-xs text-accent font-medium uppercase tracking-wide">
              {store.brand}
            </div>
          )}
        </div>
        {store.manager && (
          <div className="text-xs text-text-muted text-right">
            <div className="font-medium">{store.manager.name}</div>
            <div>{store.manager.title}</div>
            {store.manager.experience && (
              <div className="text-accent">{store.manager.experience}</div>
            )}
          </div>
        )}
      </div>

      {/* Location */}
      <p className="text-text-secondary mb-1">{store.address}</p>
      <p className="text-text-muted text-sm mb-3">{store.city}, {store.country}</p>

      {/* Specialties */}
      {store.specialties && store.specialties.length > 0 && (
        <div className="mb-3">
          <h4 className="text-sm font-medium text-primary mb-1 flex items-center gap-1">
            <span className="material-symbols-outlined text-accent text-sm">star</span>
            Spécialités:
          </h4>
          <p className="text-xs text-text-muted">{store.specialties.join(' • ')}</p>
        </div>
      )}

      {/* Features */}
      {store.features && store.features.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2 mb-4">
          {store.features.map((feature) => {
            const featureConfig = config.features?.[feature] || {};
            return (
              <span 
                key={feature} 
                className={`px-2 py-1 rounded text-xs font-medium ${
                  featureConfig.color || 'bg-accent/10 text-accent'
                }`}
              >
                {featureConfig.icon && `${featureConfig.icon} `}{feature}
              </span>
            );
          })}
        </div>
      )}

      {/* Contact and hours */}
      <div className="flex flex-col gap-2">
        <StoreHours hours={store.hours} />
        <StoreContact 
          phone={store.contact?.phone || store.phone} 
          website={store.contact?.website}
        />
      </div>

      {/* Distance indicator (if available) */}
      {store.distance !== undefined && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="text-xs text-text-muted flex items-center gap-2">
            <span className="material-symbols-outlined text-accent text-sm">straighten</span>
            <span>{store.distance.toFixed(1)} km away</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function StoreHours({ hours }) {
  // Helper function to format hours
  const formatHours = (hours) => {
    if (typeof hours === 'string') return hours;
    if (typeof hours === 'object') {
      const today = new Date().toLocaleDateString('en', { weekday: 'long' }).toLowerCase();
      return hours[today] || 'Hours not available';
    }
    return 'Hours not available';
  };

  return (
    <div className="flex items-center gap-2 text-sm text-text-secondary">
      <span className="material-symbols-outlined text-accent">schedule</span>
      <span>{formatHours(hours)}</span>
    </div>
  );
}

export function StoreContact({ phone, website }) {
  return (
    <div className="flex flex-col gap-2">
      {phone && (
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <span className="material-symbols-outlined text-accent">call</span>
          <a href={`tel:${phone}`} className="hover:underline">{phone}</a>
        </div>
      )}
      {website && (
        <div className="flex items-center gap-2 text-sm text-text-secondary">
          <span className="material-symbols-outlined text-accent">language</span>
          <a 
            href={website} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="hover:underline text-accent"
            onClick={(e) => e.stopPropagation()}
          >
            Visit Website
          </a>
        </div>
      )}
    </div>
  );
}

export default StoreCard;
